<template>
  <div>
    <app-head></app-head>
    <app-body>
      <div>
        <!-- Banner -->
        <div class="cd-banner">
          <div class="banner-content-white-text">
            <div class="case-center-card">
              <h1 class="main-title" style="xfont-family: STCaiyun, Arial, sans-serif;">{{ caseData.title }}</h1>
              <hr>
              <h2 class="sub-title">CASE STUDY</h2>
            </div>
          </div>
          <div class="breadcrumb-container">
            <el-breadcrumb separator="/">
              <el-breadcrumb-item>
                <router-link to="/"><i class="el-icon-house"></i> 首页</router-link>
              </el-breadcrumb-item>
              <el-breadcrumb-item>
                <router-link to="/customerCases">客户案例</router-link>
              </el-breadcrumb-item>
              <el-breadcrumb-item>{{ caseData.title }}</el-breadcrumb-item>
            </el-breadcrumb>
          </div>
        </div>

        <div class="cd-container">
          <!-- 摘要 -->
          <el-card class="cd-block" shadow="never">
            <div slot="header" class="cd-block-header">方案摘要</div>
            <p class="cd-summary" v-html="caseData.summary"></p>
            <div class="cd-tags">
              <el-tag v-for="t in caseData.tags" :key="t" effect="plain" class="cd-tag">{{ t }}</el-tag>
            </div>
          </el-card>

          <!-- 挑战 / 方案 / 成效 -->
          <el-card class="cd-block" shadow="never">
            <div slot="header" class="cd-block-header">
              <span class="cd-label">{{ caseData.caseLabel }}</span> {{ caseData.caseMainTitle }}
            </div>
            <p class="cd-sub">{{ caseData.caseMainDesc }}</p>
            <el-row :gutter="18" class="cd-three">
              <el-col :span="8">
                <div class="cd-mini c1">
                  <div class="cd-mini-head">01 / 挑战</div>
                  <h3>{{ caseData.challengesTitle }}</h3>
                  <ul>
                    <li v-for="(c,i) in caseData.challenges" :key="i">{{ c }}</li>
                  </ul>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="cd-mini c2">
                  <div class="cd-mini-head">02 / 方案</div>
                  <h3>{{ caseData.solutionsTitle }}</h3>
                  <ul>
                    <li v-for="(c,i) in caseData.solutions" :key="i">{{ c }}</li>
                  </ul>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="cd-mini c3">
                  <div class="cd-mini-head">03 / 成效</div>
                  <h3>{{ caseData.resultsTitle }}</h3>
                  <ul>
                    <li v-for="(c,i) in caseData.results" :key="i">{{ c }}</li>
                  </ul>
                </div>
              </el-col>
            </el-row>
          </el-card>

          <!-- 系统架构 -->
          <el-card class="cd-block" shadow="never">
            <div slot="header" class="cd-block-header">系统架构</div>
            <div class="cd-arch">
              <template v-for="(n,i) in caseData.archNodes">
                <div class="cd-node" :key="'n'+i">
                  <h4>{{ n.title }}</h4>
                  <div class="cd-node-sub">{{ n.sub }}</div>
                </div>
                <div class="cd-arrow" v-if="i < caseData.archNodes.length-1" :key="'a'+i">⟷</div>
              </template>
            </div>
            <div class="cd-note"><span class="cd-lock">🔒</span><span v-html="caseData.archNote"></span></div>
            <div class="cd-tags">
              <el-tag v-for="(t,i) in caseData.archTags" :key="i" effect="plain" class="cd-tag-arch">
                <b>{{ t.abbr }}</b> {{ t.desc }}
              </el-tag>
            </div>
          </el-card>

          <!-- 核心能力 -->
          <el-card class="cd-block" shadow="never">
            <div slot="header" class="cd-block-header">核心能力</div>
            <div class="cd-feat-grid">
              <div class="cd-feat-row" v-for="(row, ri) in featRows" :key="ri">
                <div class="cd-feat-col" v-for="(f, ci) in row" :key="ci">
                  <div class="cd-feat">
                    <div class="cd-feat-gif">
                      <template v-if="f.title === '私有化部署'">
                        <svg class="cd-feat-svg" viewBox="0 0 380 150" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="cdNode" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stop-color="#2a7c7e"/>
                              <stop offset="100%" stop-color="#0B2A4A"/>
                            </linearGradient>
                          </defs>

                          <!-- 内网边界 -->
                          <rect x="8" y="12" width="300" height="126" rx="10"
                                fill="#f2f8f8" stroke="#2a7c7e" stroke-width="1.5" stroke-dasharray="6 4"/>
                          <text x="18" y="30" font-size="12" font-weight="700" fill="#0B2A4A" font-family="'Microsoft YaHei',sans-serif">内网（私有域）</text>

                          <!-- 连接线 -->
                          <path d="M70,58 L154,43" stroke="#cbd5e1" stroke-width="1.5" fill="none"/>
                          <path d="M154,43 L238,58" stroke="#cbd5e1" stroke-width="1.5" fill="none"/>
                          <path d="M70,58 Q154,108 238,58" stroke="#cbd5e1" stroke-width="1.5" fill="none"/>

                          <!-- 流动数据包 -->
                          <circle r="3" fill="#2a7c7e">
                            <animateMotion dur="2s" repeatCount="indefinite" path="M70,58 L154,43"/>
                          </circle>
                          <circle r="3" fill="#0B2A4A">
                            <animateMotion dur="2s" repeatCount="indefinite" path="M154,43 L70,58"/>
                          </circle>
                          <circle r="3" fill="#2a7c7e">
                            <animateMotion dur="2.2s" repeatCount="indefinite" path="M154,43 L238,58"/>
                          </circle>
                          <circle r="3" fill="#0B2A4A">
                            <animateMotion dur="2.2s" repeatCount="indefinite" path="M238,58 L154,43"/>
                          </circle>
                          <circle r="3" fill="#2a7c7e">
                            <animateMotion dur="3s" repeatCount="indefinite" path="M70,58 Q154,108 238,58"/>
                          </circle>
                          <circle r="3" fill="#0B2A4A">
                            <animateMotion dur="3s" repeatCount="indefinite" path="M238,58 Q154,108 70,58"/>
                          </circle>

                          <!-- 节点：主控端 -->
                          <rect x="44" y="47" width="52" height="22" rx="5" fill="url(#cdNode)"/>
                          <text x="70" y="62" font-size="11" fill="#fff" text-anchor="middle" font-family="'Microsoft YaHei',sans-serif">主控端</text>
                          <!-- 节点：中央服务集群 -->
                          <rect x="128" y="32" width="52" height="22" rx="5" fill="url(#cdNode)"/>
                          <text x="154" y="47" font-size="11" fill="#fff" text-anchor="middle" font-family="'Microsoft YaHei',sans-serif">中央集群</text>
                          <!-- 节点：被控端 -->
                          <rect x="212" y="47" width="52" height="22" rx="5" fill="url(#cdNode)"/>
                          <text x="238" y="62" font-size="11" fill="#fff" text-anchor="middle" font-family="'Microsoft YaHei',sans-serif">被控端</text>

                          <!-- 底部说明 -->
                          <text x="158" y="132" font-size="11" font-weight="700" fill="#2a7c7e" text-anchor="middle" font-family="'Microsoft YaHei',sans-serif">数据全程不出域 · 满足等保合规</text>

                          <!-- 互联网（隔离，无连线） -->
                          <rect x="316" y="56" width="58" height="38" rx="9" fill="#f4f6f8" stroke="#cbd5e1" stroke-width="1.2"/>
                          <text x="345" y="74" font-size="11" fill="#64748b" text-anchor="middle" font-family="'Microsoft YaHei',sans-serif">🌐 互联网</text>
                          <text x="345" y="88" font-size="10" fill="#94a3b8" text-anchor="middle" font-family="'Microsoft YaHei',sans-serif">外部不可达</text>
                          <!-- 禁止外联标记 -->
                          <text x="308" y="78" font-size="16" text-anchor="middle">🚫</text>
                        </svg>
                      </template>
                      <case-feat-svg v-else-if="f.svg" :name="f.svg"></case-feat-svg>
                      <img v-else :src="featImg(ri*3 + ci + 1)" :alt="f.title"
                           @error="$event.target.parentNode.style.display='none'">
                    </div>
                    <h4>{{ f.title }}</h4>
                    <p>{{ f.desc }}</p>
                  </div>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 技术亮点 -->
          <el-card class="cd-block" shadow="never">
            <div slot="header" class="cd-block-header">技术亮点</div>
            <div class="cd-tech-grid">
              <div class="cd-tech-row" v-for="(row, ri) in techRows" :key="ri">
                <div class="cd-tech-col" v-for="(t, ci) in row" :key="ci">
                  <div class="cd-tech">
                    <span class="cd-tech-icon"><i class="el-icon-check"></i></span>
                    <div>
                      <b>{{ t.title }}</b>
                      <span class="cd-tech-desc">{{ t.desc }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 产线通讯与数据链路（仅当案例提供 modbus 数据时显示） -->
            <div class="cd-sub-block" v-if="caseData.modbus">
              <div class="cd-sub-title">产线通讯：Modbus TCP 寄存器交互</div>
              <p class="cd-sub-desc">{{ caseData.modbus.desc }}</p>
              <div class="cd-link-layout">
                <div class="cd-link-svg">
                  <case-feat-svg :name="caseData.modbus.svg || 'modbus'"></case-feat-svg>
                </div>
                <div class="cd-reg-wrap">
                  <el-table :data="caseData.modbus.registers" size="mini" border>
                    <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
                    <el-table-column prop="name" label="寄存器存储内容"></el-table-column>
                  </el-table>
                </div>
              </div>
              <div class="cd-tip"><i class="el-icon-warning-outline"></i>{{ caseData.modbus.tip }}</div>
            </div>

            <!-- 双数据库部署 -->
            <div class="cd-sub-block" v-if="caseData.databases">
              <div class="cd-sub-title">{{ caseData.databases.title }}</div>
              <p class="cd-sub-desc">{{ caseData.databases.desc }}</p>
              <div class="cd-link-layout">
                <div class="cd-link-svg">
                  <case-feat-svg :name="caseData.databases.svg || 'database'"></case-feat-svg>
                </div>
                <div class="cd-db-list">
                  <div class="cd-db-item" v-for="(d,i) in caseData.databases.items" :key="i">
                    <b>{{ d.name }}</b>
                    <span>{{ d.desc }}</span>
                  </div>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 方案信息 -->
          <el-card class="cd-block" shadow="never">
            <div slot="header" class="cd-block-header">方案信息</div>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="交付内容">{{ caseData.info.deliver }}</el-descriptions-item>
              <el-descriptions-item label="适用场景">{{ caseData.info.scene }}</el-descriptions-item>
              <el-descriptions-item label="部署方式">{{ caseData.info.deploy }}</el-descriptions-item>
              <el-descriptions-item label="交付周期">{{ caseData.info.cycle }}</el-descriptions-item>
              <el-descriptions-item label="服务保障">{{ caseData.info.service }}</el-descriptions-item>
            </el-descriptions>

            <!-- 硬件清单（仅当案例提供 hardwares 数据时显示） -->
            <div class="cd-sub-block" v-if="caseData.hardwares">
              <div class="cd-sub-title">硬件清单<span class="cd-sub-count">共 {{ caseData.hardwares.length }} 项</span></div>

              <!-- 硬件清单（一行两组，避免名称列过宽） -->
              <el-table :data="hardwareRows" size="mini" border class="cd-hw-table">
                <el-table-column label="#" width="46" align="center">
                  <template slot-scope="scope">{{ scope.row.ai }}</template>
                </el-table-column>
                <el-table-column label="设备名称">
                  <template slot-scope="scope">{{ scope.row.a.name }}</template>
                </el-table-column>
                <el-table-column label="数量" width="60" align="center">
                  <template slot-scope="scope">{{ scope.row.a.qty }}</template>
                </el-table-column>
                <el-table-column label="单位" width="60" align="center">
                  <template slot-scope="scope">{{ scope.row.a.unit }}</template>
                </el-table-column>
                <el-table-column label="#" width="46" align="center">
                  <template slot-scope="scope">{{ scope.row.b ? scope.row.bi : '' }}</template>
                </el-table-column>
                <el-table-column label="设备名称">
                  <template slot-scope="scope">{{ scope.row.b ? scope.row.b.name : '' }}</template>
                </el-table-column>
                <el-table-column label="数量" width="60" align="center">
                  <template slot-scope="scope">{{ scope.row.b ? scope.row.b.qty : '' }}</template>
                </el-table-column>
                <el-table-column label="单位" width="60" align="center">
                  <template slot-scope="scope">{{ scope.row.b ? scope.row.b.unit : '' }}</template>
                </el-table-column>
              </el-table>

            </div>
          </el-card>

          <!-- CTA -->
          <div class="cd-cta">
            <h2>{{ caseData.cta.title }}</h2>
            <p>{{ caseData.cta.desc }}</p>
            <el-button type="primary" size="medium" @click="onContact">预约演示 / 联系我们</el-button>
          </div>
        </div>
      </div>
    </app-body>
    <app-foot></app-foot>
  </div>
</template>

<script>
import AppHead from '../common/AppHeader.vue';
import AppBody from '../common/AppPageBody.vue'
import AppFoot from '../common/AppFoot.vue'
import CaseFeatSvg from './CaseFeatSvg.vue'

export default {
  name: 'CaseDetailBase',
  components: {AppHead, AppBody, AppFoot, CaseFeatSvg},
  props: {
    caseData: {type: Object, required: true}
  },
  computed: {
    featRows() {
      const list = (this.caseData && this.caseData.features) || [];
      const rows = [];
      for (let i = 0; i < list.length; i += 3) {
        rows.push(list.slice(i, i + 3));
      }
      return rows;
    },
    techRows() {
      const list = (this.caseData && this.caseData.techs) || [];
      const rows = [];
      for (let i = 0; i < list.length; i += 2) {
        rows.push(list.slice(i, i + 2));
      }
      return rows;
    },
    // 硬件清单按列排序：先填满左列，再填右列（序号纵向连续）
    hardwareRows() {
      const list = (this.caseData && this.caseData.hardwares) || [];
      const rows = [];
      const rowCount = Math.ceil(list.length / 2);
      for (let i = 0; i < rowCount; i++) {
        const a = list[i];
        const b = list[i + rowCount] || null;
        rows.push({
          a: a,
          b: b,
          ai: i + 1,
          bi: b ? i + rowCount + 1 : null,
        });
      }
      return rows;
    }
  },
  methods: {
    featImg(n) {
      const base = (this.caseData && this.caseData.imgBase) || '/customerCase/feat';
      return base + n + '.gif';
    },
    onContact() {
      this.$router.push({path: '/contact', query: {subject: 'PRODUCT'}});
    },
  },
};
</script>

<style scoped>
.cd-banner {
  background: linear-gradient(135deg, #0B2A4A, #0D4A6E);
  position: relative;
  min-height: 500px;
  margin-top: -30px
}

.banner-content-white-text {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 100%;
  padding: 0 24px
}

.case-center-card {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  border: none;
  background: transparent
}

.main-title {
  font-size: 32px;
  margin-bottom: 10px;
  font-weight: bold;
  color: #fff
}

.sub-title {
  font-size: 18px;
  color: #c0c4cc;
  font-weight: normal;
  letter-spacing: 2px;
  margin: 0
}

.breadcrumb-container {
  position: absolute;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, .5);
  padding: 8px 16px;
  border-radius: 4px;
  width: auto;
  min-width: max-content;
}

.cd-container {
  max-width: 1000px;
  margin: 40px auto 60px;
  padding: 0 24px
}

.cd-block {
  margin-bottom: 26px;
  border-radius: 8px
}

.cd-block-header {
  font-size: 18px;
  font-weight: 700;
  color: #0B2A4A;
  border-left: 3px solid #2a7c7e;
  padding-left: 10px
}

.cd-label {
  color: #2a7c7e;
  font-size: 13px;
  letter-spacing: .1em;
  margin-right: 6px
}

.cd-summary {
  font-size: 15px;
  color: #555;
  line-height: 1.9;
  text-indent: 2em;
  white-space: pre-wrap
}

.cd-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px
}

.cd-tag {
  color: #0D4A6E;
  background: rgba(13, 74, 110, .08);
  border-color: transparent
}

.cd-sub {
  font-size: 14.5px;
  color: #666;
  margin-bottom: 24px
}

.cd-three {
  margin-top: 8px
}

.cd-mini {
  border: 1px solid #e8ecf1;
  border-top: 3px solid #2b6cb0;
  border-radius: 8px;
  padding: 22px 18px;
  background: #fafbfc;
  height: 100%
}

.cd-mini.c2 {
  border-top-color: #6b46c1
}

.cd-mini.c3 {
  border-top-color: #2a7c7e
}

.cd-mini-head {
  font-size: 13px;
  font-weight: 700;
  color: #2b6cb0;
  margin-bottom: 12px
}

.cd-mini.c2 .cd-mini-head {
  color: #6b46c1
}

.cd-mini.c3 .cd-mini-head {
  color: #2a7c7e
}

.cd-mini h3 {
  font-size: 16px;
  color: #0B2A4A;
  margin-bottom: 12px
}

.cd-mini ul {
  list-style: none;
  padding: 0
}

.cd-mini li {
  font-size: 13.5px;
  color: #666;
  padding: 6px 0 6px 16px;
  position: relative
}

.cd-mini.c1 li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 12px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #2b6cb0
}

.cd-mini.c2 li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 12px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #6b46c1
}

.cd-mini.c3 li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 12px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #2a7c7e
}

.cd-arch {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap
}

.cd-node {
  background: #fff;
  border: 1.5px solid #b8cfe0;
  border-radius: 10px;
  padding: 18px 26px;
  text-align: center;
  min-width: 170px;
  max-width: 240px;
  flex: 1
}

.cd-node h4 {
  font-size: 15.5px;
  color: #0B2A4A;
  margin-bottom: 5px
}

.cd-node-sub {
  font-size: 12.5px;
  color: #888
}

.cd-arrow {
  font-size: 22px;
  color: #6ba3c7;
  font-weight: 700
}

.cd-note {
  margin-top: 20px;
  background: rgba(42, 124, 126, .05);
  border: 1.5px dashed #a8cdd0;
  border-radius: 8px;
  padding: 14px 22px;
  font-size: 13.5px;
  color: #3a5a5a;
  display: flex;
  align-items: center;
  gap: 10px
}

.cd-lock {
  font-size: 16px
}

.cd-tag-arch {
  color: #555;
  border-color: #e8ecf1;
  margin: 4px
}

.cd-tag-arch b {
  color: #2b6cb0
}

.cd-feat-grid {
  --feat-h: 250px;
  margin-top: 4px
}

.cd-feat-row {
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
  align-items: stretch
}

.cd-feat-col {
  flex: 1;
  min-width: 0;
  display: flex
}

.cd-feat {
  border: 1px solid #e8ecf1;
  border-top: 3px solid #0B2A4A;
  border-radius: 8px;
  padding: 10px 20px 20px 20px;
  background: #fff;
  transition: box-shadow .2s, border-color .2s;
  flex: 1;
  min-height: var(--feat-h);
  overflow: visible
}

.cd-feat-col:hover .cd-feat {
  box-shadow: 0 14px 44px rgba(13, 74, 110, .28);
  border-top-color: #2a7c7e
}

.cd-feat-gif {
  width: 100%;
  height: 140px;
  margin-bottom: 12px;
  border-radius: 6px;
  overflow: hidden;
  background: #f2f5f8;
  display: flex;
  align-items: center;
  justify-content: center
}

.cd-feat-gif img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block
}

.cd-feat-svg {
  width: 100%;
  height: 100%;
  display: block
}

.cd-feat h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #0B2A4A;
  margin-bottom: 8px
}

.cd-feat h4::before {
  content: '';
  display: block;
  width: 5px;
  height: 18px;
  background: linear-gradient(180deg, #2a7c7e, #0B2A4A);
  border-radius: 3px
}

.cd-feat p {
  font-size: 13px;
  color: #666;
  line-height: 1.6
}

.cd-tech-grid {
  margin-top: 4px
}

.cd-tech-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  align-items: stretch
}

.cd-tech-col {
  flex: 1;
  min-width: 0;
  display: flex
}

.cd-tech {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 20px;
  background: #f7f8fa;
  border-radius: 8px;
  transition: box-shadow .2s;
  flex: 1
}

.cd-tech:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, .08)
}

.cd-tech-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2a7c7e, #0B2A4A);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-top: 2px
}

.cd-tech b {
  display: block;
  color: #0B2A4A;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 4px
}

.cd-tech-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.6
}

.cd-cta {
  background: linear-gradient(135deg, #0B2A4A, #0D4A6E);
  border-radius: 8px;
  padding: 40px 30px;
  text-align: center;
  color: #fff;
  margin-top: 10px
}

.cd-cta h2 {
  font-size: 22px;
  margin-bottom: 10px
}

.cd-cta p {
  font-size: 14px;
  opacity: .85;
  margin-bottom: 22px
}

/* ====== 区块内子模块：硬件清单 / Modbus / 双数据库 ====== */
.cd-sub-block {
  margin-top: 26px;
  padding-top: 20px;
  border-top: 1px dashed #e8ecf1
}

.cd-sub-title {
  font-size: 15px;
  font-weight: 700;
  color: #0B2A4A;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px
}

.cd-sub-title::before {
  content: '';
  display: block;
  width: 4px;
  height: 15px;
  background: linear-gradient(180deg, #2a7c7e, #0B2A4A);
  border-radius: 3px
}

.cd-sub-count {
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8
}

.cd-sub-desc {
  font-size: 13.5px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 14px
}

.cd-link-layout {
  display: flex;
  gap: 18px;
  align-items: stretch
}

.cd-link-svg {
  flex: 1;
  min-width: 0;
  min-height: 180px;
  background: #f2f5f8;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center
}

.cd-reg-wrap {
  flex: 1;
  min-width: 0
}

.cd-db-list {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px
}

.cd-db-item {
  background: #f7f8fa;
  border-left: 3px solid #2a7c7e;
  border-radius: 6px;
  padding: 12px 16px
}

.cd-db-item b {
  display: block;
  color: #0B2A4A;
  font-size: 14px;
  margin-bottom: 3px
}

.cd-db-item span {
  font-size: 12.5px;
  color: #666
}

.cd-tip {
  margin-top: 12px;
  background: rgba(245, 158, 11, .08);
  border: 1px dashed #f59e0b;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12.5px;
  color: #92610a;
  display: flex;
  align-items: center;
  gap: 6px
}

/* 硬件清单（表格） */
.cd-hw-table {
  margin-bottom: 4px
}

@media (max-width: 800px) {
  .cd-banner {
    min-height: 360px;
  }

  .breadcrumb-container {
    bottom: 12px;
    padding: 6px 12px;
  }
  .breadcrumb-container .el-breadcrumb {
    display: inline-flex !important;
    flex-wrap: nowrap !important;
    font-size: 12px;
  }
  .breadcrumb-container .el-breadcrumb__item,
  .breadcrumb-container .el-breadcrumb__inner,
  .breadcrumb-container .el-breadcrumb__separator {
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }

  .cd-three ::v-deep .el-col {
    width: 100%;
  }

  .cd-tech-row {
    flex-direction: column;
    gap: 12px
  }

  .cd-link-layout {
    flex-direction: column
  }

  .cd-link-svg {
    width: 100%;
    height: 180px;
    min-height: 160px
  }

  .cd-feat-row {
    flex-direction: column;
    gap: 0
  }

  .cd-feat-col {
    width: 100%;
    margin-bottom: 14px
  }

  .cd-feat {
    min-height: auto;
    overflow: visible
  }

  .cd-feat-col:hover .cd-feat {
    box-shadow: 0 4px 12px rgba(0, 0, 0, .08)
  }

  .cd-arch {
    flex-direction: column
  }

  .cd-arrow {
    transform: rotate(90deg)
  }
}
</style>
